import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Platform, Text, View } from 'react-native'
import Constants from 'expo-constants'
import * as WebBrowser from 'expo-web-browser'
import * as ExpoApplication from 'expo-application'
import { ThemeContext } from '../utility/context/ThemeContext'
import { ThemedButton } from '../utility/input/buttons'
import { useAuthContext } from '../utility/context'
import LoginCarousel from '../utility/login-carousel'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Analytics from '../utility/analytics'
import { log } from '../utility/pwa'
import { MAX_CONTENT_WIDTH, SERVER, extra } from '../config'

const Login = ():React.ReactElement => {
  const theme = useContext(ThemeContext).theme

  const {
    login,
  } = useAuthContext()

  const { top, bottom } = useSafeAreaInsets()

  const [bannoSsoError, setBannoSsoError] = useState<string | null>(null)

  const climateFirstBannoIntegrationId = useMemo(() => {
    const e = extra as unknown as {
      BANNO_CLIMATE_FIRST_INTEGRATION_ID?: string
      REACT_APP_BANNO_CLIMATE_FIRST_INTEGRATION_ID?: string
    }
    return (
      e?.BANNO_CLIMATE_FIRST_INTEGRATION_ID ||
      e?.REACT_APP_BANNO_CLIMATE_FIRST_INTEGRATION_ID ||
      null
    )
  }, [])

  const startClimateFirstBannoLogin = useCallback(async () => {
    try {
      setBannoSsoError(null)

      Analytics.track('banno_login_pressed', {
        bank: 'climate_first_bank',
        integrationId: climateFirstBannoIntegrationId || undefined,
      })

      if (!climateFirstBannoIntegrationId) {
        // Misconfiguration; don't hard-crash the login screen.
        return
      }

      const params = new URLSearchParams({
        plugin: 'webapp-sso',
        target: '/dashboard',
      })

      // If we're on native, tell the server to redirect back into the app
      // (otherwise it will default to the webapp base and you'll stay in the browser).
      if (Platform.OS !== 'web') {
        const scheme =
          (Constants.expoConfig as unknown as { scheme?: string } | null)?.scheme ||
          ExpoApplication.applicationId ||
          null
        if (scheme) {
          params.set('returnTo', `${scheme}://`)
        }
      }
      const url = `${SERVER}/api/banno/${encodeURIComponent(
        String(climateFirstBannoIntegrationId)
      )}/auth?${params.toString()}`

      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.location.assign(url)
      } else {
        // Match the Auth0 mobile SDK behavior:
        // iOS uses SFSafariViewController (no "Open in app?" prompt loop),
        // Android uses Custom Tabs.
        await WebBrowser.openBrowserAsync(url, {
          enableBarCollapsing: true,
          showInRecents: true,
          presentationStyle: WebBrowser.WebBrowserPresentationStyle.FULL_SCREEN,
        })
      }
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e))
      log.error(err, { tags: { message: 'SSO startup failed' } })
      setBannoSsoError('Could not open bank sign-in. Please try again.')
    }
  }, [climateFirstBannoIntegrationId])

  return (
    <View
      style={{
        flex: 1,
        paddingTop: top || 16,
        paddingBottom: bottom || 16,
        backgroundColor: theme.colors.bgMain,
        alignItems: "center",
      }}
    >
      <View style={{flex: 1, maxWidth: MAX_CONTENT_WIDTH, width: "100%", justifyContent: "center", alignItems: "center"}}>
        <View style={{flex: 1, width: "100%", justifyContent: "center"}}>
          <LoginCarousel />
        </View>
        <View
          style={{ gap: 20, width: "100%", paddingHorizontal: 16, paddingTop: 16, alignItems: "center"}}
        >
          <ThemedButton
            text='Log In or Sign Up'
            onPress={() => {
              Analytics.track('login_or_sign_up_pressed')
              login("login")
            }}
            variant="primary"
          />
          {climateFirstBannoIntegrationId ? (
            <View style={{ width: '100%', alignItems: 'center', gap: 8 }}>
              <ThemedButton
                text='Sign in with Climate First Bank'
                onPress={startClimateFirstBannoLogin}
                variant="secondary"
                style={{
                  borderColor: theme.colors.primary,
                  backgroundColor: 'transparent',
                }}
                textStyle={{
                  color: theme.colors.primary,
                }}
                disabled={!climateFirstBannoIntegrationId}
              />
              {bannoSsoError ? (
                <Text
                  accessibilityRole="alert"
                  style={{
                    color: theme.colors.danger,
                    fontSize: 13,
                    textAlign: 'center',
                    maxWidth: 320,
                  }}
                >
                  {bannoSsoError}
                </Text>
              ) : null}
            </View>
          ) : null}
        </View>
      </View>
    </View>
  )
}

export default Login

